/**
 * The Obligation Hub workflow: Create -> Assign -> Upload -> Verify.
 *
 * Numeric values mirror the backend enums in
 * Emeis.Obligationing.Domain/AggregatesModel/Obligation.cs.
 */
import i18n from "../../i18n";
import { TONES, type ToneName } from "../../components/filters/statusColors";
import { AlarmClock, CalendarClock, FileUp, Lock, MessageCircleQuestion, ScanSearch, ShieldAlert, ShieldCheck, ShieldHalf, Timer, UserPlus, type LucideIcon } from "lucide-react";

export const ObligationStatus = {
  Upcoming: 1,
  Overdue: 2,
  PendingAssignment: 3,
  EvidenceSubmitted: 4,
  UnderVerification: 5,
  NeedMoreInfo: 6,
  Closed: 7,
} as const;
export type ObligationStatusValue =
  (typeof ObligationStatus)[keyof typeof ObligationStatus];

export const ComplianceStatus = {
  None: 0,
  Compliant: 1,
  PartiallyCompliant: 2,
  DelayedCompliant: 3,
  NonCompliant: 4,
} as const;
export type ComplianceStatusValue =
  (typeof ComplianceStatus)[keyof typeof ComplianceStatus];

export const OccurenceType = { OneTime: 1, OnGoing: 2, Recurring: 3 } as const;

export const EscalationReason = { None: 0, Unassigned: 1, Overdue: 2 } as const;

/** Escalated to the BU Head and not yet closed: once compliance is recorded the escalation has done its job. */
export function isEscalated(obligation: any): boolean {
  return (
    !!obligation?.escalatedOn &&
    Number(obligation?.obligationStatus) !== ObligationStatus.Closed
  );
}

export const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

/** A stage or compliance label in the current language, looked up when it is read. */
const metaLabel = (key: string, defaultValue: string): string =>
  i18n.t(key, { ns: "obligations", defaultValue });

type Tone = {
  label: string;
  labelKey: string;
  className: string;
  /** Filled variant, for the pill in a page header (the contract pages use the same weight). */
  solid?: string;
  /** Shared status tone (statusColors TONES) and the icon for its pill/chip. */
  tone: ToneName;
  icon: LucideIcon;
};

export const OBLIGATION_STATUS_META: Record<number, Tone> = {
  [ObligationStatus.Upcoming]: {
    labelKey: "workflow.status.upcoming",
    get label() {
      return metaLabel("workflow.status.upcoming", "Upcoming");
    },
    tone: "info",
    icon: CalendarClock,
    className: `${TONES.info.bg} ${TONES.info.text}`,
    solid: `${TONES.info.bg} ${TONES.info.text}`,
  },
  [ObligationStatus.Overdue]: {
    labelKey: "workflow.status.overdue",
    get label() {
      return metaLabel("workflow.status.overdue", "Overdue");
    },
    tone: "danger",
    icon: AlarmClock,
    className: `${TONES.danger.bg} ${TONES.danger.text}`,
    solid: `${TONES.danger.bg} ${TONES.danger.text}`,
  },
  [ObligationStatus.PendingAssignment]: {
    labelKey: "workflow.status.pendingAssignment",
    get label() {
      return metaLabel(
        "workflow.status.pendingAssignment",
        "Pending assignment",
      );
    },
    tone: "attention",
    icon: UserPlus,
    className: `${TONES.attention.bg} ${TONES.attention.text}`,
    solid: `${TONES.attention.bg} ${TONES.attention.text}`,
  },
  [ObligationStatus.EvidenceSubmitted]: {
    labelKey: "workflow.status.evidenceSubmitted",
    get label() {
      return metaLabel(
        "workflow.status.evidenceSubmitted",
        "Evidence submitted",
      );
    },
    tone: "info",
    icon: FileUp,
    className: `${TONES.info.bg} ${TONES.info.text}`,
    solid: `${TONES.info.bg} ${TONES.info.text}`,
  },
  [ObligationStatus.UnderVerification]: {
    labelKey: "workflow.status.underVerification",
    get label() {
      return metaLabel(
        "workflow.status.underVerification",
        "Under verification",
      );
    },
    tone: "decision",
    icon: ScanSearch,
    className: `${TONES.decision.bg} ${TONES.decision.text}`,
    solid: `${TONES.decision.bg} ${TONES.decision.text}`,
  },
  [ObligationStatus.NeedMoreInfo]: {
    labelKey: "workflow.status.needMoreInfo",
    get label() {
      return metaLabel("workflow.status.needMoreInfo", "Need more info");
    },
    tone: "attention",
    icon: MessageCircleQuestion,
    className: `${TONES.attention.bg} ${TONES.attention.text}`,
    solid: `${TONES.attention.bg} ${TONES.attention.text}`,
  },
  [ObligationStatus.Closed]: {
    labelKey: "workflow.status.closed",
    get label() {
      return metaLabel("workflow.status.closed", "Closed");
    },
    tone: "neutral",
    icon: Lock,
    className: `${TONES.neutral.bg} ${TONES.neutral.text}`,
    solid: `${TONES.neutral.bg} ${TONES.neutral.text}`,
  },
};

export const COMPLIANCE_STATUS_META: Record<number, Tone> = {
  [ComplianceStatus.Compliant]: {
    labelKey: "workflow.compliance.compliant",
    get label() {
      return metaLabel("workflow.compliance.compliant", "Compliant");
    },
    tone: "success",
    icon: ShieldCheck,
    className: `${TONES.success.bg} ${TONES.success.text}`,
  },
  [ComplianceStatus.PartiallyCompliant]: {
    labelKey: "workflow.compliance.partiallyCompliant",
    get label() {
      return metaLabel(
        "workflow.compliance.partiallyCompliant",
        "Partially compliant",
      );
    },
    tone: "attention",
    icon: ShieldHalf,
    className: `${TONES.attention.bg} ${TONES.attention.text}`,
  },
  [ComplianceStatus.DelayedCompliant]: {
    labelKey: "workflow.compliance.delayedCompliant",
    get label() {
      return metaLabel(
        "workflow.compliance.delayedCompliant",
        "Delayed compliant",
      );
    },
    tone: "attention",
    icon: Timer,
    className: `${TONES.attention.bg} ${TONES.attention.text}`,
  },
  [ComplianceStatus.NonCompliant]: {
    labelKey: "workflow.compliance.nonCompliant",
    get label() {
      return metaLabel("workflow.compliance.nonCompliant", "Non-compliant");
    },
    tone: "danger",
    icon: ShieldAlert,
    className: `${TONES.danger.bg} ${TONES.danger.text}`,
  },
};

/**
 * The outcome badge for an obligation as it stands. A checker reopens a closed obligation with Need More
 * Info and it keeps its earlier outcome until verified again: shown then as "Previously: …", muted, so it
 * does not read as the current result beside "Need more info".
 */
export function complianceMetaFor(
  complianceStatus: unknown,
  obligationStatus: unknown,
): (Tone & { labelOptions?: Record<string, string> }) | undefined {
  const meta = COMPLIANCE_STATUS_META[Number(complianceStatus)];
  if (!meta || !Number(obligationStatus) || Number(obligationStatus) === ObligationStatus.Closed) return meta;
  return {
    ...meta,
    labelKey: "workflow.compliance.previously",
    labelOptions: { outcome: meta.label },
    get label() {
      return i18n.t("workflow.compliance.previously", { ns: "obligations", outcome: meta.label, defaultValue: "Previously: {{outcome}}" });
    },
    tone: "neutral",
    className: `${TONES.neutral.bg} ${TONES.neutral.text}`,
  };
}

/** The statuses a user may pick by hand; Delayed Compliant is included because a late submission can be declared as such. */
export const MANUAL_COMPLIANCE_OPTIONS: ComplianceStatusValue[] = [
  ComplianceStatus.Compliant,
  ComplianceStatus.PartiallyCompliant,
  ComplianceStatus.DelayedCompliant,
  ComplianceStatus.NonCompliant,
];

export const WORKFLOW_STEPS = ["create", "assign", "upload", "verify"] as const;

/** Index into WORKFLOW_STEPS of the step the obligation is waiting on; 4 when everything is done. */
export function getWorkflowStepIndex(
  obligationStatus: number,
  complianceStatus: number,
  hasEvidence: boolean,
): number {
  if (obligationStatus === ObligationStatus.PendingAssignment) return 1;
  // A checker's question reopens verification, whatever was recorded before.
  if (obligationStatus === ObligationStatus.NeedMoreInfo) return 3;
  if (
    obligationStatus === ObligationStatus.Closed ||
    complianceStatus !== ComplianceStatus.None
  )
    return 4;
  if (
    hasEvidence ||
    obligationStatus === ObligationStatus.EvidenceSubmitted ||
    obligationStatus === ObligationStatus.UnderVerification
  )
    return 3;
  return 2;
}

/** On-going obligations wait for the Dept-Admin to choose how often they recur. */
export function isAwaitingRecurrence(
  occurenceType: number,
  recurrenceCycle: number | null | undefined,
): boolean {
  return occurenceType === OccurenceType.OnGoing && !Number(recurrenceCycle);
}

export type AssignmentRouteMode = 1 | 2;
export const AssignmentRouteMode = { Auto: 1, DeptAdmin: 2 } as const;

export interface AssignmentCandidate {
  id: string;
  name: string;
  email?: string;
}

export interface AssignmentRoute {
  businessUnitId: string;
  businessUnitName: string;
  departmentId: string;
  departmentName: string;
  mode: AssignmentRouteMode;
  assigneeId?: string | null;
  assigneeName?: string | null;
  deptUsers: AssignmentCandidate[];
  deptAdmins: AssignmentCandidate[];
}

export interface AssignmentRoutingPreview {
  availableBusinessUnits: AssignmentRoute[];
  routes: AssignmentRoute[];
}

export interface ObligationAuditLogRow {
  srNo: number;
  action: string;
  summary: string;
  userId: string;
  userName: string;
  timestamp: string;
  remarks?: string | null;
  changes: Array<{ field: string; from?: string | null; to?: string | null }>;
}

export interface ComplianceCounts {
  compliant: number;
  partiallyCompliant: number;
  delayedCompliant: number;
  nonCompliant: number;
  verified: number;
  /** Met, on time or late, as a share of what has been verified. Null when nothing has been. */
  complianceRate: number | null;
  onTimeRate: number | null;
}

export interface ObligationSummary {
  total: number;
  open: number;
  overdue: number;
  pendingAssignment: number;
  awaitingReview: number;
  needMoreInfo: number;
  dueNext7Days: number;
  escalated: number;
  compliance: ComplianceCounts;
  departments: Array<
    ComplianceCounts & {
      businessUnitId: string;
      businessUnitName: string | null;
      departmentId: string | null;
      departmentName: string | null;
      total: number;
      open: number;
      overdue: number;
    }
  >;
  overdueAgeing: Array<{ label: string; count: number }>;
  trend: Array<ComplianceCounts & { month: string }>;
}

export const CommentStatus = {
  Unresolved: 1,
  Resolved: 2,
  WontFix: 3,
} as const;
export const CommentSource = { Platform: 1, Email: 2 } as const;

export interface ObligationComment {
  id: string;
  authorId: string;
  authorName: string;
  text: string;
  status: number;
  source: number;
  emailMessageId?: string | null;
  resolvedByName?: string | null;
  resolvedOn?: string | null;
  reason?: string | null;
  creationDate: string;
}

export const EmailDirection = { Outbound: 1, Inbound: 2 } as const;
export const EmailOutcome = {
  Recorded: 0,
  AcceptedAsEvidence: 1,
  NotAccepted: 2,
} as const;

export interface ObligationEmailMessage {
  id: string;
  direction: number;
  subject: string;
  fromAddress: string;
  toAddresses: string[];
  ccAddresses: string[];
  body: string;
  attachmentFileNames: string[];
  attachmentMediaIds: string[];
  sentAt: string;
  outcome: number;
  outcomeReason?: string | null;
}

export interface ObligationEmailThread {
  emailAddress: string;
  messages: ObligationEmailMessage[];
}

/**
 * Which edit-form fields are frozen for this obligation, mirroring the server's
 * ObligationEditPolicy so the form explains a lock instead of letting the save fail.
 */
export interface ObligationEditLocks {
  /** Whole-obligation stop: nothing may be edited, and this says why. */
  blocked: string | null;
  dates: string | null;
  occurrence: string | null;
  department: string | null;
  /** Changing who it belongs to goes through Reassign, never the edit form. */
  assignee: string | null;
}

export function getObligationEditLocks(
  raw: any,
  t: (key: string, options: Record<string, unknown>) => string,
): ObligationEditLocks {
  const status = Number(raw?.obligationStatus ?? ObligationStatus.Upcoming);
  const evidenceGiven =
    Number(raw?.evidenceCount ?? 0) > 0 ||
    status === ObligationStatus.EvidenceSubmitted ||
    status === ObligationStatus.UnderVerification ||
    status === ObligationStatus.NeedMoreInfo;
  const late = !!raw?.wasOverdue || status === ObligationStatus.Overdue;
  const isSeries =
    raw?.isSeries ??
    (Number(raw?.occurenceType) !== OccurenceType.OneTime &&
      !raw?.parentObligationId);
  // A series that has opened a period: the list tells us, the row does not.
  const hasPeriods = isSeries && Number(raw?.openedPeriods ?? 0) > 0;
  const assigned = !!raw?.assigneeId && raw.assigneeId !== EMPTY_GUID;

  // Where the obligation is in its life decides whether it can be edited at all.
  // The server applies the same stops (ObligationEditPolicy); these give the reason up front.
  const deleted =
    !!raw?.deletedBy || !!raw?.deletionDate || raw?.isDeleted === true;
  const dependencyStatus = Number(raw?.dependencyStatus ?? 0);
  const blocked = deleted
    ? t("edit.lockedDeleted", {
        defaultValue: "A deleted obligation cannot be edited.",
      })
    : raw?.isActive === false
      ? t("edit.lockedInactive", {
          defaultValue:
            "A deactivated obligation cannot be edited. Activate it first.",
        })
      : raw?.parentObligationId
        ? t("edit.lockedPeriod", {
            defaultValue:
              "This is one period of a recurring obligation. Edit the recurring obligation itself.",
          })
        : status === ObligationStatus.Closed
          ? t("edit.lockedClosed", {
              defaultValue:
                "A closed obligation cannot be edited. A checker can reopen it with Need More Info.",
            })
          : status === ObligationStatus.EvidenceSubmitted ||
              status === ObligationStatus.UnderVerification
            ? t("edit.lockedUnderReview", {
                defaultValue:
                  "Evidence is being verified. Record the outcome or raise Need More Info before editing.",
              })
            : status === ObligationStatus.NeedMoreInfo
              ? t("edit.lockedNmi", {
                  defaultValue:
                    "More information has been requested from the assignee. It can be edited once the question is answered and verified.",
                })
              : null;

  return {
    blocked,
    dates:
      // A connected obligation's start is set by its Dept-Admin's confirmation once it is ready.
      dependencyStatus === 2 || dependencyStatus === 3
        ? t("edit.datesLockedConnected", {
            defaultValue:
              "This connected obligation's dates are set when its start date is confirmed. Change them from its Progress panel.",
          })
        : evidenceGiven || late
          ? t("edit.datesLockedEvidence", {
              defaultValue:
                "Dates are fixed once evidence has been submitted or the obligation is overdue.",
            })
          : hasPeriods
            ? t("edit.datesLockedPeriods", {
                defaultValue: "Dates are fixed once periods have been opened.",
              })
            : null,
    occurrence:
      hasPeriods || evidenceGiven
        ? t("edit.occurrenceLocked", {
            defaultValue:
              "The occurrence type is fixed once periods have been opened or evidence has been submitted.",
          })
        : null,
    department: assigned
      ? t("edit.departmentLocked", {
          defaultValue:
            "The business unit and department are fixed while someone is assigned. Reassign first.",
        })
      : null,
    // The server refuses an assignee change from here outright, so the routing
    // and its audit row are never skipped; this says so before the save does.
    assignee: assigned
      ? t("edit.assigneeLocked", {
          defaultValue:
            "The assignee cannot be changed here. Use Reassign on the obligation page, so the change is routed and recorded.",
        })
      : null,
  };
}

/**
 * How an obligation is named in lists: its generated number, then its title,
 * then its period ("LAWS-OBL-202609-0007-P2 · Monthly MIS"). A period's number
 * already ends in "-P{n}", so the period is only added when there is no number
 * (obligations made before numbering, until they are edited).
 */
export function obligationDisplayTitle(row: any): string {
  const title = String(row?.title || "-");
  const number = String(row?.obligationNumber || "").trim();
  if (number) return `${number} · ${title}`;
  return Number(row?.occurrenceNumber) > 0
    ? `${title} · P${row.occurrenceNumber}`
    : title;
}
